import Downshift from 'downshift';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';

const useStyles = makeStyles()({
    root: {
        flex: 1,
        position: 'relative',
        transition: 'all 0.3s ease-out',
        '@media (max-width: 720px)': {
            maxWidth: '90%'
        }
    },
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1,
        width: '100%'
    },
    paper: {
        position: 'absolute',
        borderRadius: 16,
        padding: 12,
        zIndex: 10,
        marginTop: 2,
        width: '100%',
        boxShadow: '0px 10px 40px rgba(186, 184, 176, 0.2);',
        boxSizing: 'border-box'
    },
    menuItem: {
        backgroundColor: '#FFF',
        fontWeight: 'normal'
    },
    menuItemHighlighted: {
        backgroundColor: '#F5F5F5'
    },
    menuItemSelected: {
        fontWeight: 'bold'
    }
});

const itemToString = (item) => (item ? (typeof item === 'string' ? item : item.label) : '');

const AutoComplete = (props) => {
    const { classes, cx } = useStyles();
    const {
        id,
        type,
        items,
        label,
        onUpdateInput,
        onNewRequest,
        errorText,
        hintText,
        InputProps,
        style,
        endAdornment,
        value,
        initialInputValue,
        className,
        inputRef
    } = props;

    return (
        <Downshift
            onChange={(newValue) => {
                onUpdateInput?.(itemToString(newValue));
                onNewRequest?.(newValue);
            }}
            itemToString={itemToString}
            inputValue={value}
            initialInputValue={initialInputValue}
        >
            {({ getInputProps, getItemProps, isOpen, inputValue, highlightedIndex, selectedItem, openMenu }) => (
                <div className={cx(classes.root, className)}>
                    <div className={classes.container}>
                        {/* @ts-ignore */}
                        <TextField
                            inputRef={inputRef}
                            variant="standard"
                            {...getInputProps({
                                id,
                                fullWidth: true,
                                error: !!errorText,
                                helperText: errorText,
                                label,
                                style,
                                type,
                                placeholder: hintText,
                                className: InputProps?.className,
                                onFocus: () => !isOpen && openMenu(),
                                onChange: (event) => onUpdateInput?.(event.target.value),
                                onKeyDown: (event) => {
                                    switch (event.key) {
                                        case 'Enter':
                                            // @ts-ignore
                                            onNewRequest?.(event.target.value);
                                            // @ts-ignore
                                            event.target.blur();
                                            break;
                                        case 'Escape':
                                            // @ts-ignore
                                            event.target.blur();
                                    }
                                }
                            })}
                        />
                        {endAdornment ? endAdornment : null}
                    </div>
                    {isOpen && items.length && (
                        <Paper className={classes.paper} elevation={0}>
                            {items
                                .filter(
                                    (item) =>
                                        !inputValue ||
                                        itemToString(item).toLowerCase().includes(inputValue.toLowerCase())
                                )
                                .map((item, index) => {
                                    const itemLabel = itemToString(item);
                                    return (
                                        <MenuItem
                                            key={itemLabel}
                                            {...getItemProps({ key: itemLabel, index, item })}
                                            className={cx(classes.menuItem, {
                                                [classes.menuItemHighlighted]: highlightedIndex === index,
                                                [classes.menuItemSelected]: selectedItem === item
                                            })}
                                            style={item.style}
                                        >
                                            {itemLabel}
                                        </MenuItem>
                                    );
                                })}
                        </Paper>
                    )}
                </div>
            )}
        </Downshift>
    );
};

AutoComplete.propTypes = {
    id: PropTypes.string,
    type: PropTypes.string,
    items: PropTypes.array,
    label: PropTypes.string,
    onUpdateInput: PropTypes.func,
    onNewRequest: PropTypes.func,
    errorText: PropTypes.string,
    hintText: PropTypes.string,
    InputProps: PropTypes.object,
    style: PropTypes.object,
    endAdornment: PropTypes.node,
    value: PropTypes.string,
    initialInputValue: PropTypes.string,
    className: PropTypes.string,
    inputRef: PropTypes.object
};
export default AutoComplete;
